// Inkasso efter rykkerforløbet: Collectia (inkassobureau) eller småsag ved domstolene (minretssag.dk).
// Ren beregning og tekst, så skærmen, PDF-pakken, eksporten og testene bruger det samme:
//   • foreslaaSpor: virksomhedens regel – private → reglens valg (standard Collectia), erhverv → småsag fra et beløb
//     (op til småsagsgrænsen på 50.000 kr.), ellers Collectia. Kontoret vælger altid selv til sidst.
//   • kravopgoerelse: hovedstol, rykkergebyrer (pr. rykker), kompensation (erhverv) og renter efter renteloven til dato.
//   • collectiaCsv: én række pr. sag i et enkelt format til Collectias kundeweb (Collectia har intet offentligt API).
//   • sagsfremstilling: en kort skabelon på dansk til småsagen, som kontoret retter til.
import { plusDage } from './medlemskab.ts';
import { kr, regnearkBeloeb as beloeb } from './pris.ts';
import { beregnRykker, LOV, niveauNavn, satsTekst, TOM_STATUS, type InkassoSpor, type RykkerHaendelse, type RykkerRegler, type RykkerStatus } from './rykker.ts';

export const SPOR_NAVN: Record<InkassoSpor, string> = { collectia: 'Collectia', smaasag: 'Småsag' };

/** Reglens forslag for en sag. Hovedstolen afgør, om kravet kan gå som småsag (højst 50.000 kr.). */
export function foreslaaSpor(a: { erhverv: boolean; hovedstol: number }, r: Pick<RykkerRegler, 'inkassoPrivat' | 'smaasagFra'>): InkassoSpor {
  if (!a.erhverv) return r.inkassoPrivat === 'smaasag' && a.hovedstol <= LOV.smaasagMax ? 'smaasag' : 'collectia';
  return a.hovedstol >= r.smaasagFra && a.hovedstol <= LOV.smaasagMax ? 'smaasag' : 'collectia';
}

/** Sagsnummer hos Collectia eller retten: bogstaver, tal, mellemrum og - / . (højst 40 tegn). '' = ikke tilføjet. */
export function rensSagsnr(s: string): string { return s.trim().replace(/\s+/g, ' '); }
export const gyldigtSagsnr = (s: string) => /^[A-Za-z0-9ÆØÅæøå ./-]{0,40}$/.test(rensSagsnr(s));

export type SagInput = {
  booking_id: number; kode: string; dato: string; ydelse: string; hovedstol: number; moms: number; erhverv: boolean;
  kunde: { navn: string; email: string; telefon: string; adresse: string; postnr: string; by: string; felter: Record<string, string> };
  status: RykkerStatus | null;
};
export type KravLinje = { tekst: string; dato: string | null; beloeb: number };
export type Kravopgoerelse = {
  forfald: string; idag: string; linjer: KravLinje[];
  hovedstol: number; gebyrer: number; kompensation: number; renter: number; renteDage: number; sats: number; iAlt: number;
};

type Sendt = Extract<RykkerHaendelse, { type: 'sendt' }>;
const sendteAf = (s: RykkerStatus) => s.historik.filter((h): h is Sendt => h.type === 'sendt');

/** Kravopgørelsen på dagen `idag`: én linje pr. rykkergebyr og kompensation, og renterne fra forfald til i dag. */
export function kravopgoerelse(x: Pick<SagInput, 'booking_id' | 'dato' | 'hovedstol' | 'erhverv' | 'status'>, r: RykkerRegler, idag: string): Kravopgoerelse {
  const s = x.status ?? TOM_STATUS;
  const b = beregnRykker({ dato: x.dato, hovedstol: x.hovedstol, erhverv: x.erhverv, status: s }, r, idag);
  const linjer: KravLinje[] = [{ tekst: `Hovedstol, faktura/booking ${x.booking_id} (forfald ${dkDato(b.forfald)})`, dato: x.dato, beloeb: x.hovedstol }];
  for (const h of sendteAf(s)) {
    if (h.gebyr) linjer.push({ tekst: `Rykkergebyr, ${niveauNavn(h.niveau).toLowerCase()} (renteloven § 9 b)`, dato: h.dato, beloeb: h.gebyr });
    if (h.kompensation) linjer.push({ tekst: 'Kompensation for inddrivelse, erhverv (renteloven § 9 b)', dato: h.dato, beloeb: h.kompensation });
  }
  if (b.renter) linjer.push({ tekst: `Renter ${satsTekst(r.rente)} p.a. (renteloven § 5) i ${b.renteDage} dage fra ${dkDato(b.forfald)} til ${dkDato(idag)}`, dato: idag, beloeb: b.renter });
  return { forfald: b.forfald, idag, linjer, hovedstol: x.hovedstol, gebyrer: b.gebyrer, kompensation: b.kompensation, renter: b.renter, renteDage: b.renteDage, sats: r.rente, iAlt: b.iAlt };
}

/** 2026-10-28 → 28-10-2026 (dansk datoformat i filer og dokumenter). */
export const dkDato = (d: string) => `${d.slice(8, 10)}-${d.slice(5, 7)}-${d.slice(0, 4)}`;
const cvrAf = (f: Record<string, string>) => (f.cvr ?? '').replace(/\D/g, '');

/** Kolonnerne i Collectia-filen (CSV med semikolon) – én række pr. sag. */
export const COLLECTIA_KOLONNER = [
  'Kreditornr.', 'Fakturanr.', 'Fakturadato', 'Forfaldsdato', 'Debitortype', 'Debitor navn', 'CVR-nr.', 'Adresse', 'Postnr.', 'By', 'E-mail', 'Telefon',
  'Hovedstol', 'Rykkergebyrer', 'Kompensation', 'Renter til dato', 'Rentesats % p.a.', 'Renter beregnet til', 'I alt', 'Rykkere sendt', 'Inkassovarsel sendt', 'Beskrivelse',
] as const;

export function collectiaRaekke(x: SagInput, r: RykkerRegler, idag: string): string[] {
  const k = kravopgoerelse(x, r, idag);
  const s = sendteAf(x.status ?? TOM_STATUS);
  const varsel = s.filter((h) => h.niveau === 4).at(-1);
  return [
    r.collectiaKreditor, String(x.booking_id), dkDato(x.dato), dkDato(k.forfald), x.erhverv ? 'Erhverv' : 'Privat', x.kunde.navn, cvrAf(x.kunde.felter),
    x.kunde.adresse, x.kunde.postnr, x.kunde.by, x.kunde.email, x.kunde.telefon.replace(/\s/g, ''),
    beloeb(k.hovedstol), beloeb(k.gebyrer), beloeb(k.kompensation), beloeb(k.renter), String(k.sats).replace('.', ','), dkDato(idag), beloeb(k.iAlt),
    s.map((h) => `${dkDato(h.dato)} ${niveauNavn(h.niveau)} (${h.kanaler.map((c) => (c === 'sms' ? 'SMS' : 'mail')).join('+')})`).join(', '),
    varsel ? dkDato(varsel.dato) : '', `${x.ydelse} – booking ${x.booking_id} (${x.kode})`,
  ];
}

/** Collectia-filen: semikolon, BOM og CRLF, så Excel på dansk åbner den korrekt. */
export function collectiaCsv(sager: SagInput[], r: RykkerRegler, idag: string): string {
  const felt = (v: string) => (/[;"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return '﻿' + [[...COLLECTIA_KOLONNER], ...sager.map((x) => collectiaRaekke(x, r, idag))].map((l) => l.map(felt).join(';')).join('\r\n');
}

export type Kreditor = { navn: string; cvr: string; adresse: string; telefon: string; email: string };

/** Kort sagsfremstilling til minretssag.dk. Tekst i [klammer] udfylder kontoret selv. */
export function sagsfremstilling(x: SagInput, k: Kravopgoerelse, r: RykkerRegler, kreditor: Kreditor): string {
  const s = sendteAf(x.status ?? TOM_STATUS);
  const varsel = s.filter((h) => h.niveau === 4).at(-1);
  const rykkere = s.filter((h) => h.niveau < 4).map((h) => `${niveauNavn(h.niveau).toLowerCase()} den ${dkDato(h.dato)}`);
  const adr = (a: string, p: string, b: string) => [a, [p, b].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const cvr = cvrAf(x.kunde.felter);
  const hjemmel = ['renteloven §§ 3 og 5 (renter)'];
  if (k.gebyrer) hjemmel.push('renteloven § 9 b (rykkergebyrer)');
  if (k.kompensation) hjemmel.push('renteloven § 9 b (kompensation, erhverv)');
  return [
    'SAGSØGER',
    `${kreditor.navn}${kreditor.cvr ? `, CVR ${kreditor.cvr}` : ''}${kreditor.adresse ? `, ${kreditor.adresse}` : ''}`,
    '',
    'SAGSØGTE',
    `${x.kunde.navn}${cvr ? `, CVR ${cvr}` : ''}${adr(x.kunde.adresse, x.kunde.postnr, x.kunde.by) ? `, ${adr(x.kunde.adresse, x.kunde.postnr, x.kunde.by)}` : ''}`,
    '',
    'PÅSTAND',
    `Sagsøgte tilpligtes at betale ${kr(k.iAlt)} til sagsøger med tillæg af rente efter renteloven § 5 af ${kr(k.hovedstol)} fra den ${dkDato(plusDage(k.idag, 1))}, til betaling sker.`,
    '',
    'SAGENS OMSTÆNDIGHEDER',
    `Den ${dkDato(x.dato)} udførte sagsøger ${x.ydelse.toLowerCase()} for sagsøgte (booking ${x.booking_id}). Fakturaen på ${kr(k.hovedstol)} inkl. moms forfaldt til betaling den ${dkDato(k.forfald)}.`,
    `Sagsøgte har ikke betalt trods ${rykkere.length ? rykkere.join(', ') : 'påmindelser'}${varsel ? ` og et inkassovarsel den ${dkDato(varsel.dato)} med ${r.inkassoFrist} dages frist (inkassoloven § 10)` : ''}. Rykkerne er sendt på mail og/eller SMS, jf. bilag 3.`,
    '[Beskriv kort, om sagsøgte har gjort indsigelser – fx mod arbejdet eller prisen – og hvad I har svaret. Skriv "Sagsøgte har ikke gjort indsigelser", hvis det er tilfældet.]',
    '',
    'ANBRINGENDER',
    `Sagsøgte skylder beløbet efter aftalen om ${x.ydelse.toLowerCase()}, som er udført. Kravet på renter og gebyrer støttes på ${hjemmel.join(', ')}.`,
    '',
    'BILAG',
    'Bilag 1: Kravopgørelse',
    'Bilag 2: Faktura/booking',
    'Bilag 3: Rykkere og inkassovarsel (datoer, kanal og tekst)',
    'Bilag 4: Sagsøgtes oplysninger',
    '',
    `Dato: ${dkDato(k.idag)}`,
    `${kreditor.navn}${kreditor.telefon ? ` · tlf. ${kreditor.telefon}` : ''}${kreditor.email ? ` · ${kreditor.email}` : ''}`,
  ].join('\n');
}

